import { ApplicationQuoteType, FinanceQuote, FinanceQuoteNonUniformPaymentOption } from '@amsanalytics/react-shared-library';
import { IApplication } from '../Interfaces/Application/IApplication';
import { getLeaseDeferredDownPayments } from './leaseRuleHelpers';

/** The dates the user confirms before desking, re-desking or posting. */
export interface FirstPaymentReview {
  firstPaymentLabel: string;
  firstPaymentDate: Date;
  deferredPaymentDates: Date[];
  irregularPayments: { startDate: Date; count: number; amount: number }[];
  /** "Deferred Down" or "Irregular"; empty when a lease has no deferred payments. */
  nonUniformLabel: string;
}

const byDate = (a: Date, b: Date) => a.getTime() - b.getTime();

export const getFinanceFirstPaymentReview = (quote: FinanceQuote): FirstPaymentReview => ({
  firstPaymentLabel: 'First Loan Payment',
  firstPaymentDate: new Date(quote.firstPaymentDate),
  deferredPaymentDates: quote.financeQuoteDeferredPayments.map((dp) => new Date(dp.paymentDate!)).sort(byDate),
  irregularPayments: quote.financeQuoteIrregularPaymentStreams
    .map((ip) => ({ startDate: new Date(ip.startDate!), count: ip.count, amount: ip.amount }))
    .sort((a, b) => byDate(a.startDate, b.startDate)),
  nonUniformLabel: quote.nonUniformPaymentOption === FinanceQuoteNonUniformPaymentOption.DeferredDown ? 'Deferred Down' : 'Irregular',
});

/** A Lease app's first payment and filled deferred down slots; undefined without a first payment date. */
export const getLeaseFirstPaymentReview = (application: IApplication): FirstPaymentReview | undefined => {
  const firstPaymentDate = application.applicationLeaseQuote?.firstPmtDate;
  if (!firstPaymentDate) {
    return undefined;
  }
  const deferredPaymentDates = getLeaseDeferredDownPayments(application)
    .filter((payment) => payment.date)
    .map((payment) => new Date(payment.date!))
    .sort(byDate);
  return {
    firstPaymentLabel: 'First Lease Payment',
    firstPaymentDate: new Date(firstPaymentDate),
    deferredPaymentDates,
    irregularPayments: [],
    nonUniformLabel: deferredPaymentDates.length > 0 ? 'Deferred Down' : '',
  };
};

/** A Lease app reviews its own dates; any other app reviews the selected finance quote, as before. */
export const getFirstPaymentReview = (
  application: IApplication, selectedFinanceQuote: FinanceQuote | undefined,
): FirstPaymentReview | undefined =>
  application.quoteType === ApplicationQuoteType.Lease
    ? getLeaseFirstPaymentReview(application)
    : selectedFinanceQuote && getFinanceFirstPaymentReview(selectedFinanceQuote);
